import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, CircularProgress, TextField, Typography, Popper } from '@mui/material';
import _ from 'lodash';
import makeStyles from '@mui/styles/makeStyles';

const FONTSIZE = '11px';
const useStyles = makeStyles((theme) => ({
	autoCompleteInputRoot: {
		fontSize: 'inherit',
		padding: '4px !important',
		paddingRight: '40px !important',
	},
	autoCompleteClearIndicator: {
		padding: 0,
		margin: 0,
	},
	autoCompletePopupIndicator: {
		padding: 0,
		margin: 0,
	},
	autoCompleteEndAdornment: {
		right: '0px !important',
	},
	autoCompleteListbox: {
		fontSize: FONTSIZE,
	},
	autoCompleteOption: {
		'&[data-focus="true"]': {
			backgroundColor: 'rgba(0, 0, 0, 0.2)',
		},
	},
	popper: {
		width: 'auto',
		minWidth: '280px',
		zIndex: 1300,
	},
}));

// Custom Popper component for Autocomplete to adjust listbox width dynamically
const CustomPopper = (props) => {
	const classes = useStyles();
	return <Popper {...props} className={classes.popper} placement="bottom-start" />;
};

function UserSelector({
	fetchUsers,
	label,
	totalFound,
	onUserSelect,
	hideDefaultList,
	customAPIParams = {},
	initialValue,
	...rest
}) {
	const classes = useStyles();
	const initialFormattedOption = initialValue ? initialValue : null;

	const [open, setOpen] = useState(false);
	const [inputValue, setInputValue] = useState(initialFormattedOption?.label || '');
	const [isLoading, setIsLoading] = useState(false);
	const [selectedOption, setSelectedOption] = useState(initialFormattedOption);
	const [options, setOptions] = useState(() =>
		initialFormattedOption ? [initialFormattedOption] : []
	);

	const inputValueRef = useRef('');

	const getMergedQueryParams = useCallback(
		(kw, offset) => ({
			offset,
			limit: customAPIParams.limit || 20,
			...(kw ? { keywords: kw } : {}),
			...customAPIParams,
		}),
		[customAPIParams]
	);

	const fetchUsersOnInput = useCallback(
		async (query, initVal) => {
			setIsLoading(true);
			try {
				const { data } = await fetchUsers(query);
				let newOptions = [];

				if (initVal) {
					// On init: try to find and pin the initial user
					const matchedUser = data.find((user) => user.id === initVal.id);
					if (matchedUser) {
						newOptions = [
							{
								value: matchedUser.id,
								label: `${matchedUser.firstname} ${matchedUser.lastname} <${matchedUser.email}>`,
							},
						];
						setSelectedOption(newOptions[0]);
					}
				} else {
					newOptions = data.map((user) => ({
						value: user.id,
						label: `${user.firstname} ${user.lastname} <${user.email}>`,
					}));
				}

				if (query.offset === 0) {
					setOptions((prev) => {
						const selected = prev.find((o) => o.value === selectedOption?.value);
						const freshIds = new Set(newOptions.map((o) => o.value));
						// Keep selected at top if it's not already in the new results
						const pinned = selected && !freshIds.has(selected.value) ? [selected] : [];
						return [...pinned, ...newOptions];
					});
				} else {
					setOptions((prev) => {
						const existingIds = new Set(prev.map((o) => o.value));
						return [...prev, ...newOptions.filter((o) => !existingIds.has(o.value))];
					});
				}
			} catch (error) {
				console.error('Error fetching users:', error);
			} finally {
				setIsLoading(false);
			}
		},
		[fetchUsers, selectedOption]
	);

	const debouncedFetchUserdata = useMemo(() => _.debounce(fetchUsersOnInput, 500), [
		fetchUsersOnInput,
	]);

	useEffect(() => {
		return () => debouncedFetchUserdata.cancel();
	}, [debouncedFetchUserdata]);

	const debouncedFetchMoreUsers = useMemo(
		() =>
			_.debounce((kw, currentOffset) => {
				const queryParams = getMergedQueryParams(kw, currentOffset);
				fetchUsersOnInput(queryParams);
			}, 500),
		[fetchUsersOnInput, getMergedQueryParams]
	);

	useEffect(() => {
		return () => debouncedFetchMoreUsers.cancel();
	}, [debouncedFetchMoreUsers]);

	const handleInputChange = useCallback(
		(value) => {
			inputValueRef.current = value;

			if (hideDefaultList) {
				if (value === '') {
					setInputValue('');
					setOpen(false);
					setOptions([]);
					setSelectedOption(null);
				} else {
					setInputValue(value);
					const queryParams = getMergedQueryParams(value, 0);
					debouncedFetchUserdata(queryParams);
					setOpen(true);
				}
			} else {
				setInputValue(value);
				const queryParams = getMergedQueryParams(value, 0);
				debouncedFetchUserdata(queryParams);
				setOpen(true);
			}
		},
		[debouncedFetchUserdata, getMergedQueryParams, hideDefaultList]
	);

	const handleScroll = useCallback(
		(e) => {
			const listboxNode = e.currentTarget;
			if (
				listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 1 &&
				options.length < totalFound
			) {
				// options.length is the new offset
				debouncedFetchMoreUsers(inputValueRef.current, options.length);
			}
		},
		[debouncedFetchMoreUsers, options.length, totalFound]
	);

	useEffect(() => {
		if (hideDefaultList && !selectedOption && inputValue === '') {
			setOpen(false);
		}
	}, [selectedOption, inputValue, hideDefaultList]);

	return (
		<Autocomplete
			fullWidth
			size="small"
			open={open}
			value={selectedOption}
			onClose={() => setOpen(false)}
			options={options}
			loading={isLoading}
			blurOnSelect
			classes={{
				inputRoot: classes.autoCompleteInputRoot,
				clearIndicator: classes.autoCompleteClearIndicator,
				popupIndicator: classes.autoCompletePopupIndicator,
				endAdornment: classes.autoCompleteEndAdornment,
				listbox: classes.autoCompleteListbox,
				option: classes.autoCompleteOption,
			}}
			getOptionLabel={(option) => option.label || ''}
			isOptionEqualToValue={(option, value) => option.label === value.label}
			onFocus={() => {
				if (!hideDefaultList || inputValueRef.current || selectedOption) {
					setOpen(true);
					if (!hideDefaultList) {
						// fetch unfiltered list on focus — selected user gets pinned at top
						// inside fetchUsersOnInput when it's not in the fresh results
						const queryParams = getMergedQueryParams('', 0);
						debouncedFetchUserdata(queryParams);
					}
				}
			}}
			onBlur={() => {
				setOpen(false);
				// clear typed search ref on blur so next focus starts clean
				inputValueRef.current = '';
				if (!selectedOption) {
					setInputValue('');
				}
			}}
			onInputChange={(e, value, reason) => {
				if (reason !== 'reset' && reason !== 'blur') {
					handleInputChange(value);
				}
			}}
			onChange={(e, newSelectedOption, reason) => {
				if (reason === 'selectOption' && newSelectedOption) {
					setSelectedOption(newSelectedOption);
					onUserSelect(newSelectedOption);
				} else if (reason === 'clear') {
					setSelectedOption(null);
					onUserSelect(null);
					setInputValue('');
					inputValueRef.current = '';
					if (hideDefaultList) {
						setOpen(false);
					}
				}
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					title={selectedOption ? selectedOption.label : ''}
					fullWidth
					label={label}
					slotProps={{
						input: {
							...params.InputProps,
							endAdornment: (
								<>
									{isLoading ? <CircularProgress color="inherit" size={20} /> : null}
									{params.InputProps.endAdornment}
								</>
							),
						},
					}}
				/>
			)}
			renderOption={(props, option) => (
				<li {...props} key={option.value}>
					<Typography title={option.label} variant="body2" noWrap>
						{option.label}
					</Typography>
				</li>
			)}
			slots={{
				popper: CustomPopper,
			}}
			slotProps={{
				listbox: {
					onScroll: handleScroll,
				},
			}}
			{...rest}
		/>
	);
}

UserSelector.propTypes = {
	fetchUsers: PropTypes.func.isRequired, // Function to fetch users based on input change
	label: PropTypes.string.isRequired, // Label for the input field
	totalFound: PropTypes.number.isRequired, // Total number of records found
	onUserSelect: PropTypes.func.isRequired, // Function after Selecting from List
	hideDefaultList: PropTypes.bool, // To hide the list on click (will appear after something is typed)
	customAPIParams: PropTypes.object,
	initialValue: PropTypes.object, //  {value: string, label: string}
};

export default UserSelector;
